import type { Character, PageElement } from "../state/types";

/**
 * Turns plain text into a page.
 *
 * Paragraphs are separated by blank lines and become `break` elements; each
 * whitespace-delimited token becomes a word. Empty tokens (from runs of
 * whitespace) are dropped — they would otherwise render as invisible but
 * markable spans that a reader can neither see nor deliberately hit.
 */
export const textToPage = (text: string): PageElement[] => {
  const page: PageElement[] = [];

  const paragraphs = text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const words = paragraph.split(/\s+/).filter((word) => word.length > 0);

    words.forEach((word, wordIndex) => {
      page.push({
        type: "word",
        id: `${paragraphIndex}-${wordIndex}`,
        characters: word.split("").map((character, characterIndex): Character => ({
          character,
          id: `${paragraphIndex}-${wordIndex}-${characterIndex}`,
          isMarked: false,
        })),
        isMarked: false,
        customCharacters: false,
      });
    });

    page.push({ type: "break", id: `break-after-${paragraphIndex}` });
  });

  return page;
};

/** Strips the markdown noise the extractor leaves behind. */
export const cleanExtractedText = (text: string): string =>
  text
    // Bare URLs on their own line carry no poetic content.
    .replace(/^\s*https?:\/\/\S+\s*$/gm, "")
    .replace(/[[\]()]/g, "")
    .replace(/\n\s*\n\s*\n+/g, "\n\n")
    .trim();

const SLUG_WORDS = [
  "amber",
  "ash",
  "bell",
  "birch",
  "bloom",
  "brass",
  "briar",
  "cedar",
  "chalk",
  "cinder",
  "clay",
  "cloud",
  "cove",
  "crane",
  "dawn",
  "dune",
  "dusk",
  "ember",
  "fern",
  "field",
  "flint",
  "frost",
  "glass",
  "grove",
  "harbor",
  "haze",
  "heron",
  "hollow",
  "ivory",
  "kite",
  "lantern",
  "lark",
  "linen",
  "loam",
  "marsh",
  "meadow",
  "mist",
  "moss",
  "nettle",
  "north",
  "opal",
  "orchard",
  "pine",
  "quarry",
  "quill",
  "reed",
  "ridge",
  "river",
  "rust",
  "salt",
  "sand",
  "shale",
  "shore",
  "silt",
  "slate",
  "sparrow",
  "spruce",
  "stone",
  "thistle",
  "thorn",
  "tide",
  "vale",
  "willow",
  "window",
  "winter",
];

/**
 * A short, human-readable, collision-resistant key for a new erasure — the
 * name the reader sees in their library.
 *
 * Uses `crypto.getRandomValues` where available so keys do not collide when
 * several erasures are created in the same millisecond.
 */
export const generateKey = (): string => {
  const pick = (): string => {
    const random =
      typeof crypto !== "undefined" && "getRandomValues" in crypto
        ? crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32
        : Math.random();
    return SLUG_WORDS[Math.floor(random * SLUG_WORDS.length)];
  };
  return [pick(), pick(), pick()].join("-");
};

/**
 * Whether the splash input should be treated as a link to fetch rather than as
 * text to erase directly.
 */
export const looksLikeUrl = (input: string): boolean => {
  const trimmed = input.trim();
  if (/\s/.test(trimmed)) return false;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(trimmed);
  }
};

/** Adds a scheme to bare hostnames so `example.com/article` is fetchable. */
export const normalizeUrl = (input: string): string => {
  const trimmed = input.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};
